import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import ApiService, { Credentials } from '../services/apiService';

import {
    containerStyle,
    formContainerStyle,
    headerStyle,
    titleStyle,
    formStyle,
    errorStyle,
    buttonStyle,
    footerTextStyle,
    inputStyle,
    passwordInputWrapperStyle,
    passwordInputStyle,
    passwordToggleButtonStyle
} from '../styles/globalStyles';

const Register: React.FC = () => {
    const [email, setEmail] = useState( '' );
    const [firstName, setFirstName] = useState( '' );
    const [lastName, setLastName] = useState( '' );
    const [phoneNumber, setPhoneNumber] = useState( '' );
    const [password, setPassword] = useState( '' );
    const [confirmPassword, setConfirmPassword] = useState( '' );
    const [showPassword, setShowPassword] = useState( false );
    const [showConfirmPassword, setShowConfirmPassword] = useState( false );
    const [error, setError] = useState( '' );
    const [isSubmitting, setIsSubmitting] = useState( false );

    const navigate = useNavigate();

    const credentials = useMemo< Credentials >( () => ( {
        email: process.env.REACT_APP_ADMIN_USERNAME || '',
        password: process.env.REACT_APP_ADMIN_PASSWORD || ''
    } ), [] );

    const apiService = useMemo( () => new ApiService( credentials ), [ credentials ] );

    useEffect( () => {
        const storedData = localStorage.getItem( 'pendingRegistration' );

        if( ! storedData )
            return;

        try{
            const parsedData = JSON.parse( storedData );
            setEmail( parsedData.email || '' );
            setFirstName( parsedData.name || '' );
            setLastName( parsedData.last_name || '' );
            setPhoneNumber( parsedData.phone_number || '' );
            setPassword( parsedData.password || '' );
            setConfirmPassword( parsedData.password || '' );
        }catch( err ){
            return;
        }
    }, [] );

    const handleSubmit = async ( e: React.FormEvent<HTMLFormElement> ) => {
        e.preventDefault();
        setError( '' );

        if( password !== confirmPassword ){
            setError( 'Passwords do not match.' );
            
            return;
        }

        const registrationData = {
            email: email,
            name: firstName,
            last_name: lastName,
            phone_number: phoneNumber,
            password: password
        };

        localStorage.setItem( 'pendingRegistration', JSON.stringify( registrationData ) );
        setIsSubmitting( true );

        try{
            const response = await apiService.request< boolean >( 'POST', `/api/v1/user/register/${encodeURIComponent( email )}/${encodeURIComponent( firstName )}` );

            if( ! response ){
                setError( 'Registration failed. Please try again.' );
                return;
            }

            navigate( '/verify-code' );
        }catch( err ){
            setError( 'Registration failed. Please try again.' );
        }finally{
            setIsSubmitting( false );
        }
    };

    return(
        <div style={containerStyle}>
            <div style={formContainerStyle}>
                <div style={headerStyle}>
                    <h1 style={titleStyle}>Create Account</h1>
                </div>
                <form onSubmit={handleSubmit} style={formStyle}>
                    {error && (
                        <div style={errorStyle}>
                            {error}
                        </div>
                    )}
                    <input
                        placeholder="Email Address"
                        style={inputStyle}
                        value={email}
                        onChange={( e ) => setEmail( e.target.value )}
                        type="email"
                        required
                    />
                    <input
                        placeholder="Name"
                        style={inputStyle}
                        value={firstName}
                        onChange={( e ) => setFirstName( e.target.value )}
                        type="text"
                        required
                    />
                    <input
                        placeholder="Last Name"
                        style={inputStyle}
                        value={lastName}
                        onChange={( e ) => setLastName( e.target.value )}
                        type="text"
                        required
                    />
                    <input
                        placeholder="Phone Number"
                        style={inputStyle}
                        value={phoneNumber}
                        onChange={( e ) => setPhoneNumber( e.target.value )}
                        type="tel"
                        required
                    />
                    <div style={passwordInputWrapperStyle}>
                        <input
                            placeholder="Password"
                            style={passwordInputStyle}
                            value={password}
                            onChange={( e ) => setPassword( e.target.value )}
                            type={showPassword ? 'text' : 'password'}
                            required
                        />
                        <button
                            type="button"
                            style={passwordToggleButtonStyle}
                            onClick={() => setShowPassword( ( v ) => ! v )}
                            title={showPassword ? 'Hide password' : 'Show password'}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? (
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                            ) : (
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                        </button>
                    </div>
                    <div style={passwordInputWrapperStyle}>
                        <input
                            placeholder="Confirm Password"
                            style={passwordInputStyle}
                            value={confirmPassword}
                            onChange={( e ) => setConfirmPassword( e.target.value )}
                            type={showConfirmPassword ? 'text' : 'password'}
                            required
                        />
                        <button
                            type="button"
                            style={passwordToggleButtonStyle}
                            onClick={() => setShowConfirmPassword( ( v ) => ! v )}
                            title={showConfirmPassword ? 'Hide password' : 'Show password'}
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                            {showConfirmPassword ? (
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                            ) : (
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                        </button>
                    </div>
                    <button type="submit" style={buttonStyle} disabled={isSubmitting}>
                        {isSubmitting ? 'SENDING...' : 'SUBMIT'}
                    </button>
                </form>
                <div style={{ ...footerTextStyle, display: 'flex', flexDirection: 'column' as const, alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="18" height="18" fill="none" stroke="#b0b3b8" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 17v.01"/><path d="M12 13a4 4 0 1 0-4-4"/><circle cx="12" cy="12" r="10"/></svg>
                        Your information is used only for licensing and is never shared.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default Register;
